import React from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../../components/envago/Icon/Icon';
import { ImportResultBanner } from './ImportResultBanner';
import { ImportStats } from '../ImportExcel.types';

type Props = {
    stats: ImportStats;
    skippedFileCount?: number;
};

type ReasonRow = { key: keyof ImportStats; labelKey: string; icon: string; iconClass: string };

// "Resolved" rows — rendered the same way as every other outcome row, directly
// below the progress box. Already submitted rows belong here too: nothing
// needs fixing about them, they're just a no-op, not a problem — hence the
// gray (not green) icon.
const RESOLVED_ROWS: ReasonRow[] = [
    { key: 'submittedPlausible', labelKey: 'management.import.summary.submittedPlausible', icon: Icon.Path.mdiCheckCircleOutline, iconClass: 'text-green-600' },
    { key: 'submittedImplausible', labelKey: 'management.import.summary.submittedImplausible', icon: Icon.Path.mdiCheckCircleOutline, iconClass: 'text-green-600' },
    { key: 'alreadyDone', labelKey: 'management.import.summary.reason.alreadyDone', icon: Icon.Path.mdiArrowRight, iconClass: 'text-gray-400' },
];

export const SummaryStep: React.FC<Props> = ({ stats, skippedFileCount }) => {
    const { t } = useTranslation();

    // Already-submitted rows aren't "new" readings from this file — excluding
    // them from the denominator avoids a misleadingly low ratio like "3 von 96"
    // when 78 of those 96 were correctly submitted in an earlier import already.
    const newTotal = Math.max(0, stats.total - stats.alreadyDone);
    const notImported = Math.max(0, newTotal - stats.submitted);
    const allImported = stats.submitted > 0 && notImported === 0;

    const reDownloadCount = notImported;
    const reDownloadHint = reDownloadCount > 0 ? t('management.import.summary.reDownloadHint') : undefined;

    const bannerVariant = stats.submitted > 0 ? 'success' : 'info';
    const bannerTitle = stats.submitted > 0
        ? allImported
            ? t('management.import.summary.resultTitleAllDone', { count: stats.submitted })
            : t('management.import.summary.resultTitle', { count: stats.submitted })
        : t('management.import.summary.resultTitleNone');

    const renderRow = ({ key, labelKey, icon, iconClass }: ReasonRow) => {
        const count = stats[key];
        if (!count) return null;
        return (
            <li key={key} className="flex items-start gap-1 text-sm">
                <Icon path={icon} className={`w-5 flex-shrink-0 ${iconClass}`} aria={{ 'aria-hidden': true }} />
                <span className="font-semibold tabular-nums w-8 flex-shrink-0 text-right">{count}</span>
                <span className="text-gray-700">{t(labelKey)}</span>
            </li>
        );
    };

    const hasAnyResolved = RESOLVED_ROWS.some((r) => stats[r.key] > 0);

    return (
        <div className="flex flex-col gap-4">
            <ImportResultBanner variant={bannerVariant} title={bannerTitle} description={reDownloadHint} />

            {(hasAnyResolved || notImported > 0) && (
                <ul className="flex flex-col gap-2">
                    {RESOLVED_ROWS.map(renderRow)}
                    {notImported > 0 && (
                        <li className="flex items-start gap-1 text-sm">
                            <Icon path={Icon.Path.mdiAlertCircleOutline} className="w-5 flex-shrink-0 text-orange-500" aria={{ 'aria-hidden': true }} />
                            <span className="font-semibold tabular-nums w-8 flex-shrink-0 text-right">{notImported}</span>
                            <span className="text-gray-700">{t('management.import.summary.notImportedSummary')}</span>
                        </li>
                    )}
                </ul>
            )}

            {skippedFileCount != null && skippedFileCount > 0 && (
                <div className="border-t pt-2 text-xs text-gray-500">
                    {t('management.import.summary.skippedFiles', { count: skippedFileCount })}
                </div>
            )}
        </div>
    );
};
